import React, { useState, useEffect } from "react";
import Layout from "../../layout/Main/Layout";
import { 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  IconButton,
  Chip,
  Avatar,
  LinearProgress,
  Divider
} from "@mui/material";
import {
  People as PeopleIcon,
  LocalHospital as DoctorIcon,
  Restaurant as DietIcon,
  Psychology as BrainIcon,
  Feedback as FeedbackIcon,
  QuestionAnswer as FAQIcon,
  FitnessCenter as YogaIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Visibility as ViewsIcon,
  ThumbUp as LikesIcon,
  Comment as CommentsIcon
} from "@mui/icons-material";
import { get } from "../../config/axios";
import { toastMessage } from "../../utils/toastMessage";
import LineChart from "../../components/Chart/LineChart/LineChart";
import BarChart from "../../components/Chart/BarChart/BarChart";
import styles from "./Dashboard.module.css";

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    totalUsers: 0,
    totalDoctors: 0,
    totalDietPlans: 0,
    totalBrainSessions: 0,
    totalFeedbacks: 0,
    totalFAQs: 0,
    totalYogaVideos: 0,
    recentActivity: [],
    monthlyStats: {
      users: [],
      doctors: [],
      dietPlans: [],
      brainSessions: []
    },
    userEngagement: {
      activeUsers: 0,
      sessionDuration: 0,
      bounceRate: 0
    }
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      // Fetch all dashboard data from the main stats API
      const res = await get('/api/dashboard/stats');
      const data = res?.data || {};
      setDashboardData({
        totalUsers: data.totalUsers || 0,
        totalDoctors: data.totalDoctors || 0,
        totalDietPlans: data.totalDietPlans || 0,
        totalBrainSessions: data.totalBrainSessions || 0,
        totalFeedbacks: data.totalFeedbacks || 0,
        totalFAQs: data.totalFAQs || 0,
        totalYogaVideos: data.totalYogaVideos || 0,
        recentActivity: data.recentActivity || [],
        monthlyStats: data.monthlyStats || {},
        userEngagement: data.userEngagement || { activeUsers: 0, sessionDuration: 0, bounceRate: 0 }
      });
      // Log the dashboard data for debugging
      console.log('Dashboard Data:', data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toastMessage("Error loading dashboard data", "error");
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon, color, trend, trendValue }) => (
    <Card className={styles.statCard}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" component="div" fontWeight="bold" color={color}>
              {value.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={1}>
              {title}
            </Typography>
            {trend && (
              <Box display="flex" alignItems="center" mt={1}>
                {trend === "up" ? (
                  <TrendingUpIcon color="success" fontSize="small" />
                ) : (
                  <TrendingDownIcon color="error" fontSize="small" />
                )}
                <Typography 
                  variant="caption" 
                  color={trend === "up" ? "success.main" : "error.main"}
                  ml={0.5}
                >
                  {trendValue}% from last month
                </Typography>
              </Box>
            )}
          </Box>
          <Avatar sx={{ bgcolor: color, width: 56, height: 56 }}>
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  const ActivityCard = ({ activity }) => (
    <Box className={styles.activityItem}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center">
          <Avatar 
            sx={{ 
              width: 32, 
              height: 32, 
              bgcolor: activity.type === "user" ? "#1976d2" : 
                      activity.type === "doctor" ? "#2e7d32" :
                      activity.type === "diet" ? "#ed6c02" : "#9c27b0"
            }}
          >
            {activity.type === "user" && <PeopleIcon />}
            {activity.type === "doctor" && <DoctorIcon />}
            {activity.type === "diet" && <DietIcon />}
            {activity.type === "feedback" && <FeedbackIcon />}
          </Avatar>
          <Box ml={2}>
            <Typography variant="body2" fontWeight="medium">
              {activity.message}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {activity.time}
            </Typography>
          </Box>
        </Box>
        <Chip 
          label={`+${activity.count}`} 
          size="small" 
          color="primary" 
          variant="outlined"
        />
      </Box>
    </Box>
  );

  const chartData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'Users',
        data: dashboardData.monthlyStats.users,
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        tension: 0.1,
      },
      {
        label: 'Doctors',
        data: dashboardData.monthlyStats.doctors,
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
        tension: 0.1,
      },
      {
        label: 'Diet Plans',
        data: dashboardData.monthlyStats.dietPlans,
        borderColor: 'rgb(255, 205, 86)',
        backgroundColor: 'rgba(255, 205, 86, 0.2)',
        tension: 0.1,
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Monthly Growth Analytics'
      }
    },
    scales: {
      y: {
        beginAtZero: true
      }
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box sx={{ width: '100%' }}>
          <LinearProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
          Dashboard Overview
        </Typography>
        
        {/* Key Metrics */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Patients"
              value={dashboardData.totalUsers}
              icon={<PeopleIcon />}
              color="#1976d2"
              trend="up"
              trendValue={12}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Doctors"
              value={dashboardData.totalDoctors}
              icon={<DoctorIcon />}
              color="#2e7d32"
              trend="up"
              trendValue={8}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Diet Plans"
              value={dashboardData.totalDietPlans}
              icon={<DietIcon />}
              color="#ed6c02"
              trend="up"
              trendValue={15}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="CBT Sessions"
              value={dashboardData.totalBrainSessions}
              icon={<BrainIcon />}
              color="#9c27b0"
              trend="up"
              trendValue={20}
            />
          </Grid>
        </Grid>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Feedbacks"
              value={dashboardData.totalFeedbacks}
              icon={<FeedbackIcon />}
              color="#d32f2f"
              trend="up"
              trendValue={5}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="FAQs"
              value={dashboardData.totalFAQs}
              icon={<FAQIcon />}
              color="#7b1fa2"
              trend="up"
              trendValue={10}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Yoga Modules"
              value={dashboardData.totalYogaVideos}
              icon={<YogaIcon />}
              color="#388e3c"
              trend="up"
              trendValue={25}
            />
          </Grid>
          {/* <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Views"
              value={12500}
              icon={<ViewsIcon />}
              color="#1976d2"
              trend="up"
              trendValue={18}
            />
          </Grid> */}
        </Grid>

        {/* Charts and Analytics */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} lg={8}>
            <Card className={styles.chartCard}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Growth Analytics
                </Typography>
                <Box sx={{ height: 400 }}>
                  <LineChart data={chartData} options={chartOptions} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} lg={4}>
            <Card className={styles.activityCard}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Recent Activity
                </Typography>
                <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                  {dashboardData.recentActivity.map((activity, index) => (
                    <ActivityCard key={index} activity={activity} />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Additional Analytics */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card className={styles.analyticsCard}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  User Engagement
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Active Users</Typography>
                    <Typography variant="body2" fontWeight="bold">{dashboardData.userEngagement.activeUsers}</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={dashboardData.userEngagement.activeUsers} sx={{ mb: 2 }} />
                  
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Session Duration</Typography>
                    <Typography variant="body2" fontWeight="bold">{dashboardData.userEngagement.sessionDuration} min</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={dashboardData.userEngagement.sessionDuration * 6} sx={{ mb: 2 }} />
                  
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Bounce Rate</Typography>
                    <Typography variant="body2" fontWeight="bold">{dashboardData.userEngagement.bounceRate}%</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={dashboardData.userEngagement.bounceRate} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          {/* <Grid item xs={12} md={6}>
            <Card className={styles.analyticsCard}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Content Performance
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <LikesIcon color="primary" sx={{ mr: 1 }} />
                    <Box flex={1}>
                      <Typography variant="body2">Total Likes</Typography>
                      <Typography variant="h6" fontWeight="bold">2,450</Typography>
                    </Box>
                  </Box>
                  
                  <Box display="flex" alignItems="center" mb={2}>
                    <CommentsIcon color="secondary" sx={{ mr: 1 }} />
                    <Box flex={1}>
                      <Typography variant="body2">Total Comments</Typography>
                      <Typography variant="h6" fontWeight="bold">1,230</Typography>
                    </Box>
                  </Box>
                  
                  <Box display="flex" alignItems="center">
                    <ViewsIcon color="success" sx={{ mr: 1 }} />
                    <Box flex={1}>
                      <Typography variant="body2">Total Views</Typography>
                      <Typography variant="h6" fontWeight="bold">12,500</Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid> */}
        </Grid>
      </Box>
    </Layout>
  );
};

export default Dashboard; 